import { useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, Trash2, Clock3, Edit3, Send, X, Instagram, Facebook, Youtube, Eye, Music } from "lucide-react";
import DraftEditDialog from "@/components/DraftEditDialog";
import DraftPreviewDialog from "@/components/DraftPreviewDialog";
import AudioTrimControls from "@/components/AudioTrimControls";

const PLATFORM_META = {
  instagram: { label: "Instagram", Icon: Instagram },
  facebook: { label: "Facebook", Icon: Facebook },
  youtube: { label: "YouTube", Icon: Youtube },
};

function statusBadge(status) {
  const map = {
    draft: "badge-yellow",
    scheduled: "badge-amber",
    posted: "badge-green",
    rejected: "badge-red",
    error: "badge-red",
  };
  return <span className={`badge ${map[status] || ""}`}>{status}</span>;
}

// Human-readable progress label per render engine. Used while a draft's video
// is being generated so the user knows exactly which engine is running and how
// long it typically takes.
const RENDER_ENGINE_LABEL = {
  fast: { name: "Veo 3.1 Fast", eta: "1-3 min" },
  pro:  { name: "Veo 3.1 Pro",  eta: "3-6 min" },
};

export default function DraftCard({ draft, onChange, compact = false, clicks = 0 }) {
  const [open, setOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadLabel, setUploadLabel] = useState("");
  const audioRef = useRef(null);

  const update = async (patch) => {
    setBusy(true);
    try {
      await api.patch(`/drafts/${draft.draft_id}`, patch);
      await onChange?.();
    } catch {
      toast.error("Update failed");
    } finally {
      setBusy(false);
    }
  };

  const uploadAudio = async (file) => {
    if (!file) return;
    if (!draft.video_url) {
      toast.error("Add or generate a video first — audio can only be attached to video posts.");
      return;
    }
    if (file.size > 200 * 1024 * 1024) {
      toast.error("Audio too large (max 200MB)");
      return;
    }
    setUploadingAudio(true);
    setUploadPct(0);
    setUploadLabel(`Uploading 0% · 0 / ${(file.size / 1024 / 1024).toFixed(1)} MB`);
    try {
      const fd = new FormData();
      fd.append("audio", file);
      await api.post(`/drafts/${draft.draft_id}/audio`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 20 * 60 * 1000, // 20 min for slow connections
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const pct = Math.round((evt.loaded * 100) / evt.total);
          const mbUp = (evt.loaded / 1024 / 1024).toFixed(1);
          const mbTot = (evt.total / 1024 / 1024).toFixed(1);
          setUploadPct(pct);
          setUploadLabel(
            pct >= 100
              ? "Processing audio on server…"
              : `Uploading ${pct}% · ${mbUp} / ${mbTot} MB`
          );
        },
      });
      toast.success(`Attached ${file.name}`);
      await onChange?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Audio upload failed";
      toast.error(typeof detail === "string" ? detail : "Audio upload failed");
    } finally {
      setUploadingAudio(false);
      setUploadPct(0);
      setUploadLabel("");
      if (audioRef.current) audioRef.current.value = "";
    }
  };

  const [publishStage, setPublishStage] = useState(null);

  const approve = () => update({ status: "approved" }).then(() => toast.success("Approved & scheduled"));
  const reject = () => update({ status: "rejected" }).then(() => toast("Rejected"));
  const publishNow = async () => {
    setBusy(true);
    setPublishStage(null);
    // Poll the draft's publish_stage every 2s while the request is in flight so
    // the user sees concrete progress instead of a generic spinner. Only IG
    // emits stages today (FB/YT publish synchronously and finish too fast).
    let stop = false;
    const poll = async () => {
      while (!stop) {
        try {
          const r = await api.get(`/drafts/${draft.draft_id}`);
          const st = r.data?.publish_stage || null;
          setPublishStage(st);
        } catch { /* ignore */ }
        await new Promise((res) => setTimeout(res, 2000));
      }
    };
    if (draft.platform === "instagram") poll();
    try {
      await api.post(`/drafts/${draft.draft_id}/publish-now`, null, { timeout: 5 * 60 * 1000 });
      toast.success(`Posted to ${draft.platform}`);
      await onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    } finally {
      stop = true;
      setPublishStage(null);
      setBusy(false);
    }
  };
  const del = async () => {
    setBusy(true);
    try {
      await api.delete(`/drafts/${draft.draft_id}`);
      await onChange?.();
      toast("Deleted");
    } finally {
      setBusy(false);
    }
  };
  const cancelRender = async () => {
    setBusy(true);
    try {
      await api.post(`/drafts/${draft.draft_id}/cancel-render`);
      toast("Render cancelled");
      await onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally {
      setBusy(false);
    }
  };
  const retryRender = async () => {
    setBusy(true);
    try {
      await api.post(`/drafts/${draft.draft_id}/retry-render?speed=ray2`);
      toast.success("Re-rendering with Ray 2 Quick · 1-2 min");
      await onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    } finally {
      setBusy(false);
    }
  };

  const meta = PLATFORM_META[draft.platform] || { label: draft.platform, Icon: Instagram };
  const Icon = meta.Icon;

  return (
    <>
      <article
        className="panel overflow-hidden flex flex-col"
        data-testid={`draft-card-${draft.draft_id}`}
      >
        <div className="relative aspect-[16/10] overflow-hidden bg-[#0A0A0A]">
          {draft.video_url ? (
            <video
              src={`${process.env.REACT_APP_BACKEND_URL}${draft.video_url}`}
              className="w-full h-full object-cover"
              muted
              loop
              playsInline
              preload="metadata"
              onMouseEnter={(e) => e.currentTarget.play().catch(() => {})}
              onMouseLeave={(e) => { e.currentTarget.pause(); e.currentTarget.currentTime = 0; }}
            />
          ) : draft.image_url ? (
            <img
              src={draft.image_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${draft.image_url}` : draft.image_url}
              alt="Generated draft visual"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/20 label-mono">No visual</div>
          )}
          {draft.video_status === "rendering" && (() => {
            const eng = RENDER_ENGINE_LABEL[draft.render_speed] || { name: "AI video", eta: "a few min" };
            return (
              <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center gap-3 p-4 text-center">
                <div className="w-10 h-10 border-2 border-white/20 border-t-[#FF3B00] rounded-full animate-spin" />
                <div className="label-mono text-white">
                  {eng.name} is rendering · {eng.eta}
                </div>
                <div className="label-mono text-white/40">
                  Style: {draft.video_style || "—"}
                </div>
                <button
                  type="button"
                  onClick={cancelRender}
                  disabled={busy}
                  className="mt-1 px-3 py-1.5 border border-white/20 text-xs font-mono uppercase tracking-wider text-white/70 hover:text-white hover:border-white/45 transition-colors disabled:opacity-50"
                  data-testid={`cancel-render-${draft.draft_id}`}
                >
                  Stop rendering
                </button>
              </div>
            );
          })()}
          {draft.video_status === "failed" && (
            <div className="absolute inset-0 bg-red-950/70 backdrop-blur-sm flex flex-col items-center justify-center gap-3 p-6">
              <div className="label-mono text-red-300">AI video failed</div>
              <div className="text-xs text-white/70 text-center max-w-xs leading-relaxed">
                {draft.last_error || "AI video generation failed"}
              </div>
              <div className="flex gap-2 mt-1">
                <button
                  type="button"
                  onClick={retryRender}
                  disabled={busy}
                  className="px-3 py-1.5 border border-[#FF3B00] bg-[#FF3B00]/15 text-xs font-mono uppercase tracking-wider text-[#FF3B00] hover:bg-[#FF3B00]/25 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                  data-testid={`retry-render-${draft.draft_id}`}
                >
                  <Send size={11} /> Retry · Ray 2 Quick
                </button>
                <button
                  type="button"
                  onClick={del}
                  disabled={busy}
                  className="px-3 py-1.5 border border-white/25 text-xs font-mono uppercase tracking-wider text-white/80 hover:text-white hover:border-white/50 transition-colors disabled:opacity-50"
                  data-testid={`delete-failed-${draft.draft_id}`}
                >
                  Delete draft
                </button>
              </div>
            </div>
          )}
          {busy && publishStage && (
            <div className="absolute inset-0 bg-black/75 backdrop-blur-sm flex flex-col items-center justify-center gap-4 p-6 text-center" data-testid={`publish-progress-${draft.draft_id}`}>
              <div className="label-mono text-white/80">Posting to Instagram</div>
              <div className="w-full max-w-[220px] space-y-2">
                {[
                  { id: "creating", label: "1 · Sending media to Meta" },
                  { id: "meta_processing", label: "2 · Meta is processing the video" },
                  { id: "publishing", label: "3 · Publishing to your feed" },
                ].map((s) => {
                  const order = ["creating", "meta_processing", "publishing"];
                  const isDone = order.indexOf(publishStage) > order.indexOf(s.id);
                  const isCurrent = publishStage === s.id;
                  return (
                    <div
                      key={s.id}
                      data-testid={`publish-stage-${s.id}`}
                      className={`flex items-center gap-2 text-xs font-mono ${
                        isCurrent ? "text-[#FF3B00]" : isDone ? "text-[#00FF66]" : "text-white/35"
                      }`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${
                        isCurrent ? "bg-[#FF3B00] animate-pulse" :
                        isDone ? "bg-[#00FF66]" : "bg-white/25"
                      }`} />
                      {s.label}
                      {isCurrent && draft.publish_stage_info?.poll && (
                        <span className="text-white/45">· {draft.publish_stage_info.poll}/{draft.publish_stage_info.max_polls}</span>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="label-mono text-white/35 max-w-xs leading-relaxed">
                Meta&rsquo;s reel processing usually finishes in 30-90 seconds. We&rsquo;ll auto-publish the moment it&rsquo;s ready.
              </div>
            </div>
          )}
          <div className="absolute top-3 left-3 flex items-center gap-2">
            <span className="badge flex items-center gap-1.5 bg-black/70 backdrop-blur-sm border-white/20">
              <Icon size={11} /> {meta.label}
            </span>
            {draft.video_url && (
              <span className="badge bg-black/70 backdrop-blur-sm border-[#FF3B00]/50 text-[#FF3B00]">▶ VIDEO</span>
            )}
            {draft.video_style && (
              <span className="badge bg-black/70 backdrop-blur-sm border-white/20 uppercase">{draft.video_style}</span>
            )}
          </div>
          <div className="absolute top-3 right-3">{statusBadge(draft.status)}</div>
        </div>

        <div className="p-5 flex flex-col flex-1">
          <div className="flex items-center justify-between mb-3">
            <div className="label-mono truncate">
              {draft.content_type} · {draft.release_title}
            </div>
            <div className="label-mono text-white/35">
              {draft.scheduled_at ? new Date(draft.scheduled_at).toLocaleDateString() : "—"}
            </div>
          </div>
          <p
            className={`text-sm leading-relaxed text-white/75 ${compact ? "line-clamp-3" : ""}`}
            style={{ whiteSpace: "pre-wrap" }}
          >
            {draft.caption}
          </p>
          {draft.hashtags?.length > 0 && !compact && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {draft.hashtags.map((h) => (
                <span key={h} className="font-mono text-[10px] text-white/55 uppercase tracking-wider">{h}</span>
              ))}
            </div>
          )}

          {draft.audio_url && (
            <div className="mt-3 flex items-center gap-2 label-mono text-white/60">
              <Music size={11} className="text-[#FF3B00]" />
              <span className="truncate" data-testid={`draft-audio-${draft.draft_id}`}>
                ♪ {draft.audio_filename || "Custom audio"}
              </span>
            </div>
          )}

          {draft.audio_url && draft.status !== "posted" && (
            <AudioTrimControls draft={draft} onChange={onChange} />
          )}

          {uploadingAudio && (
            <div className="mt-3 p-3 border border-[#FF3B00]/40 bg-[#FF3B00]/5 rounded-sm">
              <div className="label-mono text-white/80 mb-2" data-testid={`audio-upload-label-${draft.draft_id}`}>
                {uploadLabel || "Uploading…"}
              </div>
              <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#FF3B00] transition-all"
                  style={{ width: `${uploadPct}%` }}
                />
              </div>
            </div>
          )}

          <input
            ref={audioRef}
            type="file"
            accept="audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/mp4,audio/m4a,audio/x-m4a,audio/aac,audio/ogg,audio/flac"
            className="hidden"
            onChange={(e) => uploadAudio(e.target.files?.[0])}
            data-testid={`draft-audio-input-${draft.draft_id}`}
          />

          <div className="flex flex-wrap gap-2 mt-5 pt-5 border-t border-white/[0.06]">
            {draft.status !== "posted" && draft.video_status !== "rendering" && (
              <button
                className="btn-ghost-small"
                onClick={() => setPreviewOpen(true)}
                data-testid={`preview-draft-${draft.draft_id}`}
              >
                <Eye size={12} /> Preview
              </button>
            )}
            {draft.status !== "posted" && draft.video_url && draft.video_status !== "rendering" && (
              <button
                className="btn-ghost-small"
                onClick={() => audioRef.current?.click()}
                disabled={uploadingAudio}
                data-testid={`add-audio-${draft.draft_id}`}
              >
                <Music size={12} />
                {uploadingAudio ? "Uploading…" : draft.audio_url ? "Replace music" : "Add music"}
              </button>
            )}
            {draft.status === "draft" && (
              <>
                <button
                  className="btn-amber-small"
                  onClick={approve}
                  disabled={busy}
                  data-testid={`approve-draft-${draft.draft_id}`}
                >
                  <Check size={12} /> Approve
                </button>
                <button
                  className="btn-ghost-small"
                  onClick={() => setOpen(true)}
                  disabled={busy}
                  data-testid={`edit-draft-${draft.draft_id}`}
                >
                  <Edit3 size={12} /> Edit
                </button>
                <button
                  className="btn-danger-small"
                  onClick={reject}
                  disabled={busy}
                  data-testid={`reject-draft-${draft.draft_id}`}
                >
                  <X size={12} /> Reject
                </button>
              </>
            )}
            {draft.status === "scheduled" && (
              <>
                <button
                  className="btn-amber-small"
                  onClick={publishNow}
                  disabled={busy}
                  data-testid={`post-now-${draft.draft_id}`}
                >
                  <Send size={12} /> {busy ? "Posting…" : "Post now"}
                </button>
                <button
                  className="btn-ghost-small"
                  onClick={() => setOpen(true)}
                  disabled={busy}
                >
                  <Edit3 size={12} /> Edit
                </button>
                <button className="btn-danger-small" onClick={del} disabled={busy}>
                  <Trash2 size={12} /> Remove
                </button>
              </>
            )}
            {draft.status === "posted" && (
              <div className="flex flex-col gap-1 w-full">
                <div className="flex items-center gap-4 label-mono flex-wrap">
                  <span data-testid={`draft-likes-${draft.draft_id}`}>Likes {(draft.engagement?.likes ?? 0).toLocaleString()}</span>
                  <span data-testid={`draft-reach-${draft.draft_id}`}>Reach {(draft.engagement?.reach ?? 0).toLocaleString()}</span>
                  <span data-testid={`draft-comments-${draft.draft_id}`}>Comments {(draft.engagement?.comments ?? 0).toLocaleString()}</span>
                  <span className="text-[#FF3B00]" data-testid={`draft-clicks-${draft.draft_id}`}>
                    CTA clicks {clicks.toLocaleString()}
                  </span>
                </div>
                {draft.last_metrics_at && (
                  <div className="label-mono text-white/30">
                    Metrics updated {new Date(draft.last_metrics_at).toLocaleTimeString()}
                  </div>
                )}
              </div>
            )}
            {draft.status === "error" && (
              <div className="flex flex-col gap-2 w-full">
                <div className="label-mono text-red-400 break-all" data-testid={`draft-error-${draft.draft_id}`}>
                  {draft.last_error || "Publish failed"}
                </div>
                <div className="flex gap-2">
                  <button
                    className="btn-amber-small"
                    onClick={publishNow}
                    disabled={busy}
                    data-testid={`retry-post-${draft.draft_id}`}
                  >
                    <Send size={12} /> Retry
                  </button>
                  <button className="btn-ghost-small" onClick={() => update({ status: "draft" })} disabled={busy}>
                    <Clock3 size={12} /> Back to draft
                  </button>
                  <button className="btn-danger-small" onClick={del} disabled={busy}>
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
            )}
            {draft.status === "rejected" && (
              <>
                <button
                  className="btn-ghost-small"
                  onClick={() => update({ status: "draft" })}
                  data-testid={`requeue-draft-${draft.draft_id}`}
                >
                  <Clock3 size={12} /> Re-queue
                </button>
                <button
                  className="btn-danger-small"
                  onClick={del}
                  disabled={busy}
                  data-testid={`delete-rejected-${draft.draft_id}`}
                >
                  <Trash2 size={12} /> Delete
                </button>
              </>
            )}
          </div>
        </div>
      </article>
      <DraftEditDialog draft={draft} open={open} onOpenChange={setOpen} onSaved={onChange} />
      <DraftPreviewDialog draft={draft} open={previewOpen} onOpenChange={setPreviewOpen} />
    </>
  );
}
